import styles from "./CancelEventDialog.module.scss";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { WarningIcon } from "../Icons/Warning";
import { useAlert } from "../../context/AlertContext";
import { fetcher } from "../../utils/fetcher";
import { useEnumStore } from "../../store/enumStore";
import { useEffect, useState } from "react";

interface Event {
  id: string;
  event_title?: string;
  [key: string]: any;
}

interface CancelEventDialogProps {
  event: Event | null;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  onSuccessCallback?: () => void;
}

export const CancelEventDialog = ({
  event,
  dialogOpen,
  setDialogOpen,
  onSuccessCallback,
}: CancelEventDialogProps) => {
  const { setAlert } = useAlert();
  const { cancellationReasons, fetchCancellationReasons } = useEnumStore();
  const [selectedReason, setSelectedReason] = useState<string>("");

  useEffect(() => {
    if (dialogOpen && cancellationReasons.length === 0) {
      fetchCancellationReasons();
    }
  }, [dialogOpen, cancellationReasons.length, fetchCancellationReasons]);

  const handleEventCancel = async () => {
    if (!event) return;

    if (!selectedReason) {
      setAlert(true, "warning", "Prosím vyberte důvod zrušení akce.");
      return;
    }

    try {
      await fetcher({
        url: `/event/cancel-event/${event.id}?reason_of_cancellation=${selectedReason}`,
        method: "POST",
      });
      setAlert(
        true,
        "success",
        "Vaše akce byla zrušena a účastníkům bylo odesláno oznámení.",
      );
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    } catch (error) {
      setAlert(
        true,
        "error",
        "Zrušení akce se nezdařilo, zkuste to prosím znovu později.",
      );
      console.error("Error canceling event:", error);
    } finally {
      setDialogOpen(false);
      setSelectedReason("");
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedReason("");
        }}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        maxWidth="xs"
        className={styles.dialog}
      >
        <DialogTitle id="cancel-dialog-title" className={styles.dialogTitle}>
          <WarningIcon />
          Opravdu chcete akci zrušit?
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <DialogContentText id="cancel-dialog-description">
            Zrušením se akce smaže a nebude možné ji dohledat. Všem přihlášeným
            pošleme mail o jejím zrušení.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="cancellation-reason-label">
              Důvod zrušení *
            </InputLabel>
            <Select
              labelId="cancellation-reason-label"
              id="cancellation-reason-select"
              value={selectedReason}
              label="Důvod zrušení *"
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              {cancellationReasons.map((reason) => (
                <MenuItem key={reason.id} value={reason.value}>
                  {reason.label_cz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={() => {
              setDialogOpen(false);
              setSelectedReason("");
            }}
          >
            Ne, nerušit
          </Button>
          <Button
            variant="contained"
            fullWidth
            color="error"
            disabled={!selectedReason}
            onClick={() => handleEventCancel()}
          >
            Ano, zrušit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
